import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { ALWAYS_ENABLED_WHEN_AVAILABLE_CONTEXT_FIELDS } from '@secca/shared/helpers/atlas/atlas-context-field-settings.helper';
import {
  AtlasContextFieldSettings,
  AtlasContextFieldValues,
  AtlasContextInputsDialogData
} from '@secca/shared/models/atlas/atlas-chatbot.model';

type AtlasContextFieldKey = keyof AtlasContextFieldSettings & keyof AtlasContextFieldValues;

interface AtlasContextFieldConfig {
  key: AtlasContextFieldKey;
  label: string;
  sourceLabel: string;
  sourceClass: string;
}

@Component({
  selector: 'app-atlas-chatbot-context-inputs-dialog',
  templateUrl: './atlas-chatbot-context-inputs-dialog.component.html',
  styleUrls: ['./atlas-chatbot-context-inputs-dialog.component.scss'],
  standalone: false
})
export class AtlasChatbotContextInputsDialogComponent {
  readonly contextFields: AtlasContextFieldConfig[] = [
    { key: 'customerProfile', label: 'Customer Profile', sourceLabel: 'Insurance', sourceClass: 'context-source-insurance' },
    { key: 'productName', label: 'Product Name', sourceLabel: 'Insurance', sourceClass: 'context-source-insurance' },
    { key: 'insurancePeriodFrom', label: 'Insurance Period From', sourceLabel: 'Insurance', sourceClass: 'context-source-insurance' },
    { key: 'insurancePeriodTo', label: 'Insurance Period To', sourceLabel: 'Insurance', sourceClass: 'context-source-insurance' },
    { key: 'reporter', label: 'Reporter', sourceLabel: 'Case', sourceClass: 'context-source-case' },
    { key: 'endUserNationality', label: 'End-user Nationality', sourceLabel: 'Case', sourceClass: 'context-source-case' },
    { key: 'incidentCauseLevel1', label: 'Incident Cause Level 1', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'incidentCauseLevel2', label: 'Incident Cause Level 2', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'incidentCauseLevel3', label: 'Incident Cause Level 3', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'incidentLocation', label: 'Incident Location', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'incidentDate', label: 'Incident Date', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'winterSportEnabled', label: 'Winter Sport Enabled', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'incidentDescription', label: 'Incident Description', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'mainDiagnosis', label: 'Main Diagnosis', sourceLabel: 'Incident', sourceClass: 'context-source-incident' },
    { key: 'departureDate', label: 'Departure Date', sourceLabel: 'Planned Travel', sourceClass: 'context-source-travel' },
    { key: 'arrivalDate', label: 'Arrival Date', sourceLabel: 'Planned Travel', sourceClass: 'context-source-travel' },
    { key: 'travelDestination', label: 'Destinations', sourceLabel: 'Planned Travel', sourceClass: 'context-source-travel' },
    { key: 'coTravellers', label: 'Co-travellers', sourceLabel: 'Planned Travel', sourceClass: 'context-source-travel' },
    { key: 'purposeOfTravel', label: 'Purpose of Travel', sourceLabel: 'Planned Travel', sourceClass: 'context-source-travel' }
  ];
  @Output() readonly settingsChanged = new EventEmitter<AtlasContextFieldSettings>();
  settings: AtlasContextFieldSettings;
  values: AtlasContextFieldValues;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AtlasContextInputsDialogData,
    private dialogRef: MatDialogRef<AtlasChatbotContextInputsDialogComponent>,
    public dialogBoundryService: DialogBoundryService
  ) {
    this.settings = { ...data.settings };
    this.values = { ...data.values };
  }

  close(): void {
    this.dialogRef.close();
  }

  hasFieldValue(field: AtlasContextFieldKey): boolean {
    const value = this.values?.[field];
    return !!(value && value.trim());
  }

  isAlwaysEnabledField(field: AtlasContextFieldKey): boolean {
    return (ALWAYS_ENABLED_WHEN_AVAILABLE_CONTEXT_FIELDS as string[]).includes(field);
  }

  isFieldToggleDisabled(field: AtlasContextFieldKey): boolean {
    return !this.hasFieldValue(field) || this.isAlwaysEnabledField(field);
  }

  toggleSetting(field: AtlasContextFieldKey): void {
    if (this.isFieldToggleDisabled(field)) {
      return;
    }

    this.settings = {
      ...this.settings,
      [field]: !this.settings[field]
    };
    this.settingsChanged.emit({ ...this.settings });
  }

  trackByContextField(_index: number, field: AtlasContextFieldConfig): AtlasContextFieldKey {
    return field.key;
  }

  enableAllAvailableFields(): void {
    this.setAllAvailableFields(true);
  }

  disableAllAvailableFields(): void {
    this.setAllAvailableFields(false);
  }

  applyCaseData(
    caseNumber: string | null | undefined,
    values: AtlasContextFieldValues,
    settings: AtlasContextFieldSettings
  ): void {
    this.data.caseNumber = caseNumber || '-';
    this.values = { ...values };
    this.settings = { ...settings };
  }

  private setAllAvailableFields(enabled: boolean): void {
    const nextSettings = { ...this.settings };
    this.contextFields.forEach(field => {
      if (this.hasFieldValue(field.key)) {
        nextSettings[field.key] = this.isAlwaysEnabledField(field.key) ? true : enabled;
      }
    });

    this.settings = nextSettings;
    this.settingsChanged.emit({ ...this.settings });
  }
}
